import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../../config/firebase";
import SubscriptionService from "../../../services/SubscriptionService";

type RefreshData = (userId: string, scope: string) => Promise<unknown>;

export function useSubscribeCheckoutReturn({
  userId,
  email,
  refetchStripeStatus,
  refreshData,
}: {
  userId: string | null;
  email: string | null | undefined;
  refetchStripeStatus: () => void | Promise<void>;
  refreshData: RefreshData;
}) {
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      const sid = query.get("session_id");
      setSuccess(true);
      setSessionId(sid ?? "");

      if (userId) {
        const linkSession = async () => {
          const functions = getFunctions(app, "europe-west1");
          if (sid) {
            try {
              const linkStripeSession = httpsCallable(
                functions,
                "linkStripeSession"
              );
              await linkStripeSession({ session_id: sid });
            } catch (err) {
              console.warn("linkStripeSession failed:", err);
            }
          } else if (email) {
            try {
              const linkByEmail = httpsCallable(
                functions,
                "linkStripeCustomerByEmail"
              );
              await linkByEmail();
            } catch (err) {
              console.warn("linkStripeCustomerByEmail failed:", err);
            }
          }

          if (!SubscriptionService.isInitialized) {
            try {
              await SubscriptionService.initialize();
            } catch {
              /* non-fatal */
            }
          }

          const { createTeamIfNeeded } =
            await import("../../../utils/teams/postPurchaseTeamCreation");
          const delay = (ms: number) =>
            new Promise<void>((resolve) => setTimeout(resolve, ms));

          for (let attempt = 0; attempt < 6; attempt++) {
            if (attempt > 0) await delay(1000);
            const result = await createTeamIfNeeded(
              userId,
              email ?? undefined
            );
            if (result.success && !result.skipped) {
              await refreshData(userId, "all").catch(() => {});
              break;
            }
            if (result.skipped && result.reason === "User already has a team") {
              break;
            }
            if (
              result.skipped &&
              result.reason === "No active subscription-details"
            ) {
              continue;
            }
            if (result.skipped) {
              break;
            }
            break;
          }
        };
        void linkSession().then(() => {
          void refetchStripeStatus();
        });
      }
    }

    if (query.get("canceled")) {
      setSuccess(false);
      setMessage(
        "Order canceled -- continue to shop around and checkout when you're ready."
      );
    }
  }, [userId, email, refetchStripeStatus, refreshData]);

  return { message, success, sessionId };
}
